"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.processOrderItemForTax = exports.computeOrderItemSurcharge = exports.updateItemAdjustmentQuantity = exports.computeOrderItemDiscount = exports.computeOrderItemTotal = exports.computeLineCost = exports.computeLineTotal = exports.computeTotalOrderItemsAmountWithReward = exports.computeOrderItemValueWithReward = exports.computeTotalOrderItemsAmount = exports.computeOrderItemValue = exports.computeOrderTotals = exports.computeOrderItemTaxes = exports.flatToPercentageForAdjustment = exports.limitAdjustmentToMaxAmount = exports.flatToPercentage = exports.getOrderTotal = exports.applyDiscounts = exports.calculateTotalInclusiveTax = exports.calculateTotalExclusiveTax = exports.calculateTotalPaidAmount = exports.calculateTotalTaxRate = exports.filterUnPaidOrderItem = exports.filterActiveOrderItem = void 0;
const lodash_mergewith_1 = __importDefault(require("lodash.mergewith"));
const domain_1 = require("@oolio-group/domain");
const utils_1 = require("./utils");
const decimal_js_1 = __importDefault(require("decimal.js"));
/**
 * Validates if a payment is complete or not.
 * @param payment OrderPayment
 * @returns boolean
 */
function isPaymentComplete(payment) {
    if (payment.status === undefined ||
        payment.status === domain_1.OrderPaymentStatus.COMPLETE) {
        return true;
    }
    return false;
}
function filterActiveOrderItem(item) {
    var _a;
    return (item.status !== domain_1.OrderItemStatus.CANCELLED &&
        item.status !== domain_1.OrderItemStatus.VOID &&
        !((_a = item.parentCombo) === null || _a === void 0 ? void 0 : _a.id) &&
        item.status !== domain_1.OrderItemStatus.TRANSFERRED);
}
exports.filterActiveOrderItem = filterActiveOrderItem;
function filterUnPaidOrderItem(item) {
    return item.paymentStatus !== domain_1.OrderPaymentStatus.COMPLETE;
}
exports.filterUnPaidOrderItem = filterUnPaidOrderItem;
/**
 * Calculate the total tax rate based on the rates of a list of taxes.
 * @param taxes Tax[]
 */
function calculateTotalTaxRate(taxes) {
    return taxes.reduce((acc, x) => acc + ((x === null || x === void 0 ? void 0 : x.rate) || 0) / 100, 0);
}
exports.calculateTotalTaxRate = calculateTotalTaxRate;
/**
 * Calculate the total amount paid .
 * @param payments OrderPayment[]
 */
function calculateTotalPaidAmount(payments) {
    return payments
        .filter(payment => isPaymentComplete(payment))
        .reduce((sum, payment) => sum + payment.amount, 0);
}
exports.calculateTotalPaidAmount = calculateTotalPaidAmount;
/**
 * Calculate the total tax based on total tax rate
 * when the price is exclusive of tax
 *
 * tax = itemTotal * rate
 *
 * @param taxRate number
 * @param price number
 * @param quantity number
 */
function calculateTotalExclusiveTax(taxRate, price, quantity = 1) {
    return (taxRate !== null && taxRate !== void 0 ? taxRate : 0) * (price !== null && price !== void 0 ? price : 0) * quantity;
}
exports.calculateTotalExclusiveTax = calculateTotalExclusiveTax;
/**
 * Calculate the total tax based on total tax rate
 * when the price is inclusive of tax
 *
 * tax = itemTotal * rate/(1 + rate)
 *
 * @param taxRate number
 * @param price number
 * @param quantity number
 */
function calculateTotalInclusiveTax(taxRate, price, quantity = 1) {
    taxRate = taxRate !== null && taxRate !== void 0 ? taxRate : 0;
    return ((price !== null && price !== void 0 ? price : 0) * quantity * taxRate) / (1 + taxRate);
}
exports.calculateTotalInclusiveTax = calculateTotalInclusiveTax;
/**
 * Applies discounts to a price sequentially
 * @param price price before discount
 * @param discounts list of discounts to apply
 */
function applyDiscounts(price, discounts) {
    if (!discounts || discounts.length === 0) {
        return price || 0;
    }
    if (isNaN(price)) {
        return 0;
    }
    return discounts.reduce((total, discount) => {
        let amount = 0;
        switch (discount.type) {
            case domain_1.DiscountType.PERCENTAGE:
                amount = total * discount.amount * 0.01;
                break;
            case domain_1.DiscountType.FLAT:
                amount = discount.amount;
            default:
                break;
        }
        return total - amount;
    }, price);
}
exports.applyDiscounts = applyDiscounts;
/**
 * Calculate the totalPrice
 * when subTotal and discount variation is given
 *
 * @param subTotal sub total of the order
 * @param variation order discount variation
 */
function getOrderTotal(subTotal, variation) {
    return (0, utils_1.limitDecimalCount)((subTotal || 0) * (variation || 1));
}
exports.getOrderTotal = getOrderTotal;
/**
 * Converts a flat discount to percentage discount
 * @param discount to convert
 * @param totalPrice total price to which discount will be applied
 */
function flatToPercentage(input, totalPrice) {
    if (input && input.type === domain_1.DiscountType.FLAT) {
        return {
            type: domain_1.DiscountType.PERCENTAGE,
            amount: (input.amount / totalPrice) * 100,
        };
    }
    return input;
}
exports.flatToPercentage = flatToPercentage;
/**
 * In percentage adjustment, if amount is more than the maximum amount,
 * convert flat maxAmount to percentage.
 * @param input
 * @param totalPrice
 * @returns Adjustment
 * NOTE: This is applicable only for discounts
 */
function limitAdjustmentToMaxAmount(input, totalPrice) {
    if (input &&
        input.adjustmentUnit == domain_1.AdjustmentUnit.PERCENTAGE &&
        (input.adjustmentType === domain_1.AdjustmentType.DISCOUNT ||
            input.adjustmentType === domain_1.AdjustmentType.REWARD) &&
        !isNaN(input.maximumAmount) &&
        input.maximumAmount !== null &&
        input.maximumAmount !== 0) {
        let adjustedAmount = new decimal_js_1.default(input.amount || 0).times(input.quantity || 1);
        adjustedAmount = adjustedAmount.times(totalPrice).times(0.01);
        if (decimal_js_1.default.abs(adjustedAmount).toNumber() >
            decimal_js_1.default.abs(input.maximumAmount).toNumber()) {
            const sign = decimal_js_1.default.sign(input.amount);
            const a = {
                ...input,
                amount: decimal_js_1.default.abs(input.maximumAmount)
                    .div(input.quantity || 1) // account for adjustment quantity
                    .div(totalPrice)
                    .times(100)
                    .times(sign)
                    .toNumber(), // converting amount to number to be consistent
            };
            return a;
        }
    }
    return input;
}
exports.limitAdjustmentToMaxAmount = limitAdjustmentToMaxAmount;
function flatToPercentageForAdjustment(input, totalPrice) {
    if (input && (input === null || input === void 0 ? void 0 : input.adjustmentUnit) == domain_1.AdjustmentUnit.FLAT) {
        const a = {
            ...input,
            adjustmentUnit: domain_1.AdjustmentUnit.PERCENTAGE,
            amount: (input.amount / totalPrice) * 100,
        };
        return a;
    }
    return input;
}
exports.flatToPercentageForAdjustment = flatToPercentageForAdjustment;
function mergeTaxRecords(record1, ...rest) {
    return (0, lodash_mergewith_1.default)(record1, ...rest, (a, b) => ({
        tax: (a === null || a === void 0 ? void 0 : a.tax) || (b === null || b === void 0 ? void 0 : b.tax),
        amount: (0, utils_1.sumDecimals)([
            (0, utils_1.limitDecimalCount)((a === null || a === void 0 ? void 0 : a.amount) || 0),
            (0, utils_1.limitDecimalCount)((b === null || b === void 0 ? void 0 : b.amount) || 0),
        ]),
    }));
}
/**
 * Calculate amount for each tax in an order item,
 * Creates a record of tax and it's total amount
 * @param item order item to apply taxes on
 */
function computeOrderItemTaxes(item) {
    const orderItemAdjustments = (item === null || item === void 0 ? void 0 : item.adjustments) || [];
    const itemWithoutModifiers = { ...item, modifiers: [] };
    const lineTotal = computeLineTotal(itemWithoutModifiers);
    const discountItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, { adjustmentType: domain_1.AdjustmentType.DISCOUNT });
    const surchargeItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, {
        adjustmentType: domain_1.AdjustmentType.SURCHARGE,
        escapePaymentType: true,
    });
    const rewardItemValue = (0, utils_1.getItemRewardValue)(itemWithoutModifiers, orderItemAdjustments);
    const adjustmentValue = discountItemValue + surchargeItemValue + rewardItemValue;
    const itemValue = lineTotal + adjustmentValue;
    const itemTaxes = (item.taxes || [])
        .map(tax => {
        if (!tax)
            return;
        const rate = calculateTotalTaxRate([tax]);
        const amount = item.taxInclusive
            ? calculateTotalInclusiveTax(rate, itemValue, 1)
            : calculateTotalExclusiveTax(rate, itemValue, 1);
        return { [tax.id]: { tax, amount } };
    })
        .filter(tax => !!tax);
    const modifierTaxes = (item.modifiers || []).map(x => computeOrderItemTaxes({
        ...x,
        // TODO: Add logic if modifier can have 'taxInclusive' later a separate key.
        taxInclusive: item.taxInclusive,
        adjustments: (item === null || item === void 0 ? void 0 : item.adjustments) || [],
    }));
    return mergeTaxRecords({}, ...itemTaxes, ...modifierTaxes);
}
exports.computeOrderItemTaxes = computeOrderItemTaxes;
/**
 * Compute order total
 * @param orderItems list of items in order
 * @param discounts list of discounts to be applied to the order
 */
function computeOrderTotals(order) {
    const { orderItems, adjustments } = order || {};
    if (!(orderItems && typeof orderItems === 'object' && orderItems.length)) {
        return {
            amountDue: 0,
            discountAmount: 0,
            subTotal: 0,
            taxes: [],
            totalPrice: 0,
            totalTax: 0,
            surchargeAmount: 0,
            rewardDiscountAmount: 0,
            tip: 0,
            totalPaymentAmount: 0,
        };
    }
    const activeItems = orderItems.filter(filterActiveOrderItem);
    let totalTax = 0;
    let totalPrice = 0;
    let totalPayableTax = 0;
    let orderDiscountAmount = 0;
    let rewardDiscountAmount = 0;
    let taxRecord = {};
    let orderSurchargeAmount = 0;
    let totalPaymentAmount = 0;
    const lineTotals = activeItems.map(x => computeOrderItemValue(x));
    const subTotal = (0, utils_1.sumDecimals)(lineTotals);
    // when adjustment is flat, convert to percentage
    let orderAdjustments = (adjustments || []).map(x => flatToPercentageForAdjustment(x, subTotal));
    // maxAmount checker needed for discounts
    orderAdjustments = (orderAdjustments || []).map(x => limitAdjustmentToMaxAmount(x, subTotal));
    // order level reward value
    // calculate with maximum discount amount base on order's subtotal
    rewardDiscountAmount = (0, utils_1.sumDecimals)([
        rewardDiscountAmount,
        (0, utils_1.limitDecimalCount)((0, utils_1.getAdjustmentValue)(subTotal, orderAdjustments, {
            adjustmentType: domain_1.AdjustmentType.REWARD,
        })),
    ]);
    activeItems.forEach(item => {
        /*
        orderAdjustmentsOnSubTotal will apply on subTotal
        orderAdjustmentsOnAdjustment will apply on subTotal + any adjustments (Except service charge)
        */
        const orderAdjustmentsOnSubTotal = orderAdjustments.filter(a => !a.doNotIncludeInSalesAmount);
        const orderAdjustmentsOnAdjustment = orderAdjustments.filter(a => a.doNotIncludeInSalesAmount);
        let itemAdjustments = ((item === null || item === void 0 ? void 0 : item.adjustments) || []).map(x => flatToPercentageForAdjustment(x, computeLineTotal(item)));
        // maxAmount checker needed for percentage discounts/rewards on items
        itemAdjustments = (itemAdjustments || []).map(x => limitAdjustmentToMaxAmount(x, computeLineTotal(item)));
        const itemSubTotal = computeLineTotal(item);
        const itemAmountDue = (0, utils_1.sumDecimals)([
            itemSubTotal,
            (0, utils_1.getAdjustmentValue)(itemSubTotal, itemAdjustments, {
                adjustmentType: domain_1.AdjustmentType.DISCOUNT,
            }),
            (0, utils_1.getAdjustmentValue)(itemSubTotal, itemAdjustments, {
                adjustmentType: domain_1.AdjustmentType.SURCHARGE,
            }),
            (0, utils_1.getItemRewardValue)(item, itemAdjustments),
        ]);
        const processedItemForTax = processOrderItemForTax(item);
        const taxes = computeOrderItemTaxes({
            ...processedItemForTax,
            adjustments: orderAdjustmentsOnSubTotal,
        });
        // Add tax amounts from item and modifiers
        const itemTotalTax = +Object.values(taxes).reduce((s, x) => (0, utils_1.sumDecimals)([s, (0, utils_1.limitDecimalCount)(x.amount)]), 0);
        if (!item.taxInclusive) {
            totalPayableTax = (0, utils_1.sumDecimals)([totalPayableTax, itemTotalTax]);
        }
        const discountValueOnSubTotal = (0, utils_1.getAdjustmentValue)(itemAmountDue, orderAdjustmentsOnSubTotal, {
            adjustmentType: domain_1.AdjustmentType.DISCOUNT,
        });
        const surchargeValueOnSubTotal = (0, utils_1.getAdjustmentValue)(itemAmountDue, orderAdjustmentsOnSubTotal, {
            adjustmentType: domain_1.AdjustmentType.SURCHARGE,
            escapePaymentType: true,
        });
        const rewardValueOnSubTotal = (0, utils_1.getAdjustmentValue)(itemAmountDue, orderAdjustmentsOnSubTotal, {
            adjustmentType: domain_1.AdjustmentType.REWARD,
            escapePaymentType: true,
        });
        const netItemAmountDue = (0, utils_1.sumDecimals)([
            itemAmountDue,
            discountValueOnSubTotal,
            surchargeValueOnSubTotal,
            rewardValueOnSubTotal,
        ]);
        const discountValueOnAdjustment = (0, utils_1.getAdjustmentValue)(netItemAmountDue, orderAdjustmentsOnAdjustment, {
            adjustmentType: domain_1.AdjustmentType.DISCOUNT,
        });
        const surchargeValueOnAdjustment = (0, utils_1.getAdjustmentValue)(netItemAmountDue, orderAdjustmentsOnAdjustment, {
            adjustmentType: domain_1.AdjustmentType.SURCHARGE,
            escapePaymentType: true,
        });
        // item level reward value, calculated on original line total
        totalTax = (0, utils_1.sumDecimals)([totalTax, itemTotalTax]);
        taxRecord = mergeTaxRecords(taxRecord, taxes);
        orderDiscountAmount = (0, utils_1.sumDecimals)([
            orderDiscountAmount,
            discountValueOnSubTotal,
            discountValueOnAdjustment,
        ]);
        orderSurchargeAmount = (0, utils_1.sumDecimals)([
            orderSurchargeAmount,
            surchargeValueOnSubTotal,
            surchargeValueOnAdjustment,
        ]);
        rewardDiscountAmount = (0, utils_1.sumDecimals)([rewardDiscountAmount]);
    });
    //problem with multiple payment surcharges,payment amounts include handling fee.
    // Keeping complete payments only for paid amount calculations.
    //should have handling fee attribute instead
    const { paymentSurcharge, paidAmount } = (0, utils_1.getPaymentSurchargeValueAndPaidAmount)(
    //payment surcharge should be flat or have the payment amount for percentage calculation
    order.payments || []);
    const deliveryFee = (order === null || order === void 0 ? void 0 : order.deliveryFee) || 0;
    const serviceCharge = (order === null || order === void 0 ? void 0 : order.serviceCharge) || 0;
    const tip = (0, utils_1.getTipsFromPayments)(order.payments || []) || (order === null || order === void 0 ? void 0 : order.tip) || 0;
    //use same precision (2 d p) to calculate total price for consistency
    //total price should be the summed components shown, else there will be variance
    totalPrice = (0, utils_1.sumDecimals)([
        totalPrice,
        (0, utils_1.limitDecimalCount)(totalPayableTax),
        (0, utils_1.limitDecimalCount)(subTotal),
        (0, utils_1.limitDecimalCount)(orderSurchargeAmount),
        (0, utils_1.limitDecimalCount)(orderDiscountAmount),
        (0, utils_1.limitDecimalCount)(rewardDiscountAmount),
    ]);
    totalPaymentAmount = (0, utils_1.sumDecimals)([
        totalPrice,
        deliveryFee,
        serviceCharge,
        tip,
        paymentSurcharge,
    ]);
    return {
        totalTax: (0, utils_1.limitDecimalCount)(totalTax),
        subTotal: (0, utils_1.limitDecimalCount)(subTotal),
        totalPrice: (0, utils_1.limitDecimalCount)(totalPrice),
        amountDue: (0, utils_1.limitDecimalCount)((0, utils_1.sumDecimals)([
            (0, utils_1.limitDecimalCount)(totalPaymentAmount),
            -(0, utils_1.limitDecimalCount)(paidAmount),
        ])),
        taxes: Object.values(taxRecord)
            .filter(x => x.amount > 0)
            .map(y => {
            return { ...y, amount: (0, utils_1.limitDecimalCount)(y.amount) };
        }),
        surchargeAmount: (0, utils_1.limitDecimalCount)(orderSurchargeAmount),
        discountAmount: (0, utils_1.limitDecimalCount)(Math.abs(orderDiscountAmount)),
        rewardDiscountAmount: (0, utils_1.limitDecimalCount)(Math.abs(rewardDiscountAmount)),
        tip: (0, utils_1.limitDecimalCount)(tip),
        totalPaymentAmount: (0, utils_1.limitDecimalCount)(totalPaymentAmount),
    };
}
exports.computeOrderTotals = computeOrderTotals;
/**
 *
 * @param orderItem order item
 * @param discountVariation discount variation of the order
 */
function computeOrderItemValue(orderItem, discountVariation) {
    const lineTotal = computeLineTotal(orderItem);
    const orderItemAdjustments = (orderItem === null || orderItem === void 0 ? void 0 : orderItem.adjustments) || [];
    const discountItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, {
        adjustmentType: domain_1.AdjustmentType.DISCOUNT,
    });
    const surchargeItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, {
        adjustmentType: domain_1.AdjustmentType.SURCHARGE,
        escapePaymentType: true,
    });
    const paymentSurcharge = (0, utils_1.getPaymentAdjustmentValue)(lineTotal, orderItemAdjustments);
    const rewardItemValue = (0, utils_1.getItemRewardValue)(orderItem, orderItemAdjustments);
    const itemValue = (0, utils_1.sumDecimals)([
        lineTotal,
        discountItemValue,
        surchargeItemValue,
        paymentSurcharge,
        rewardItemValue,
    ]);
    const orderDiscountPerItem = (1 - (discountVariation || 1)) * itemValue;
    return itemValue - orderDiscountPerItem;
}
exports.computeOrderItemValue = computeOrderItemValue;
function computeTotalOrderItemsAmount(orderItems) {
    return orderItems.reduce((sum, item) => {
        sum += computeOrderItemValue(item);
        return sum;
    }, 0);
}
exports.computeTotalOrderItemsAmount = computeTotalOrderItemsAmount;
function computeOrderItemValueWithReward(orderItem, discountVariation) {
    const lineTotal = computeLineTotal(orderItem);
    const orderItemAdjustments = (orderItem === null || orderItem === void 0 ? void 0 : orderItem.adjustments) || [];
    const discountItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, {
        adjustmentType: domain_1.AdjustmentType.DISCOUNT,
    });
    const surchargeItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, {
        adjustmentType: domain_1.AdjustmentType.SURCHARGE,
        escapePaymentType: true,
    });
    const rewardItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, {
        adjustmentType: domain_1.AdjustmentType.REWARD,
        escapePaymentType: true,
    });
    const paymentSurcharge = (0, utils_1.getPaymentAdjustmentValue)(lineTotal, orderItemAdjustments);
    const itemValue = lineTotal +
        discountItemValue +
        surchargeItemValue +
        paymentSurcharge +
        rewardItemValue;
    const orderDiscountPerItem = (1 - (discountVariation || 1)) * itemValue;
    return itemValue - orderDiscountPerItem;
}
exports.computeOrderItemValueWithReward = computeOrderItemValueWithReward;
/**
 * calculate order item total amount with reward
 * @param orderItems order items
 */
function computeTotalOrderItemsAmountWithReward(orderItems) {
    return orderItems.reduce((sum, item) => {
        sum += computeOrderItemValueWithReward(item);
        return sum;
    }, 0);
}
exports.computeTotalOrderItemsAmountWithReward = computeTotalOrderItemsAmountWithReward;
const getModifiersTotal = (modifiers) => {
    return (modifiers || []).reduce((total, x) => total + (x.quantity || 1) * (x.unitPrice || 0), 0);
};
/**
 * calculate order item line total
 * Product( qty * unitPrice) + Sum(Modifier(qty * unitPrice))
 * @param item order item
 */
function computeLineTotal(item) {
    var _a, _b;
    if (!item ||
        isNaN(item.quantity) ||
        isNaN(item.unitPrice) ||
        ((_a = item === null || item === void 0 ? void 0 : item.parentCombo) === null || _a === void 0 ? void 0 : _a.id)) {
        return 0;
    }
    const comboProductsTotal = ((_b = item.comboItems) === null || _b === void 0 ? void 0 : _b.reduce((total, comboItem) => {
        const productAmount = comboItem.unitPrice || 0, productQuantity = comboItem.quantity || 1;
        return (total +
            productAmount * productQuantity +
            productQuantity * getModifiersTotal(comboItem.modifiers));
    }, 0)) || 0;
    const productTotal = (comboProductsTotal + item.unitPrice) * item.quantity;
    const modifierTotal = getModifiersTotal(item.modifiers) * item.quantity;
    return productTotal + modifierTotal;
}
exports.computeLineTotal = computeLineTotal;
/**
 * calculate order item cost
 * Product(qty * costPrice)
 * also takes into modifiers
 * @param item order item
 */
function computeLineCost(item) {
    if (!item || isNaN(item.quantity) || isNaN(item.costPrice)) {
        return 0;
    }
    // TODO: Add modifiers costPrice too (when we support it)
    return (0, utils_1.limitDecimalCount)(item.costPrice * item.quantity);
}
exports.computeLineCost = computeLineCost;
/**
 * calculate order item line total
 * Product( qty * unitPrice) and do not consider modifiers
 * used in the UI for cart items total
 * @param item order item
 */
function computeOrderItemTotal(item) {
    if (!item || isNaN(item.quantity) || isNaN(item.unitPrice)) {
        return 0;
    }
    const productTotal = item.unitPrice * item.quantity;
    return (0, utils_1.limitDecimalCount)(productTotal);
}
exports.computeOrderItemTotal = computeOrderItemTotal;
function computeOrderItemDiscount(item) {
    const lineTotal = computeLineTotal(item);
    updateItemAdjustmentQuantity(item);
    const orderItemAdjustments = (item === null || item === void 0 ? void 0 : item.adjustments) || [];
    const discountItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, {
        adjustmentType: domain_1.AdjustmentType.DISCOUNT,
    });
    const discountRewardValue = (0, utils_1.getItemRewardValue)(item, orderItemAdjustments);
    return Math.abs(discountItemValue) + Math.abs(discountRewardValue);
}
exports.computeOrderItemDiscount = computeOrderItemDiscount;
// We're setting the item adjustment quantity equal to item quantity for the advanced discount
function updateItemAdjustmentQuantity(item) {
    var _a;
    const orderItemAdjustment = (_a = item === null || item === void 0 ? void 0 : item.adjustments) === null || _a === void 0 ? void 0 : _a.find(a => a.isDiscountRuleEnabled &&
        a.adjustmentType === domain_1.AdjustmentType.DISCOUNT &&
        a.adjustmentUnit === domain_1.AdjustmentUnit.FLAT);
    if (orderItemAdjustment) {
        orderItemAdjustment.quantity = item.quantity;
    }
}
exports.updateItemAdjustmentQuantity = updateItemAdjustmentQuantity;
function computeOrderItemSurcharge(item) {
    const lineTotal = computeLineTotal(item);
    const orderItemAdjustments = (item === null || item === void 0 ? void 0 : item.adjustments) || [];
    const surchargeItemValue = (0, utils_1.getAdjustmentValue)(lineTotal, orderItemAdjustments, {
        adjustmentType: domain_1.AdjustmentType.SURCHARGE,
    });
    return surchargeItemValue;
}
exports.computeOrderItemSurcharge = computeOrderItemSurcharge;
function processOrderItemForTax(item) {
    const itemValue = computeOrderItemValue({ ...item, modifiers: [] });
    const processedModifiers = (item.modifiers || []).map(x => {
        const updatedModifier = processOrderItemForTax({
            ...x,
            // actual modifier quantity = item.quantity * modifier.quantity
            quantity: x.quantity * item.quantity,
            //when modifier structure changes check if it is taxinclusive
            taxInclusive: item.taxInclusive,
            adjustments: (item === null || item === void 0 ? void 0 : item.adjustments) || [],
        });
        return {
            ...x,
            quantity: updatedModifier.quantity,
            unitPrice: updatedModifier.unitPrice,
        };
    });
    return {
        ...item,
        unitPrice: itemValue,
        quantity: 1,
        modifiers: processedModifiers,
        comboItems: [],
    };
}
exports.processOrderItemForTax = processOrderItemForTax;
//# sourceMappingURL=computePrice.js.map